import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

function RegisterFields({ form, setForm, ageConfirmed, setAgeConfirmed }) {
  return (
    <>
      <input data-testid="auth-display-name-input" required maxLength={80} autoComplete="name" placeholder="Display name" className="brutal-input w-full px-4 py-3"
        value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
      <input data-testid="auth-username-input" required minLength={3} maxLength={30} pattern="[a-z0-9][a-z0-9_]{2,29}" autoComplete="username" placeholder="@username" className="brutal-input w-full px-4 py-3"
        title="3–30 characters; start with a letter or number; use lowercase letters, numbers, or underscores"
        value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} />
      <input data-testid="auth-email-input" required type="email" placeholder="Email" className="brutal-input w-full px-4 py-3"
        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <input data-testid="auth-password-input" required type="password" autoComplete="new-password" placeholder="Password (12+ characters)" minLength={12} className="brutal-input w-full px-4 py-3"
        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      <input data-testid="auth-referral-input" placeholder="Referral code (optional) — both get 30 days of Premium" className="brutal-input w-full px-4 py-3 tracking-widest uppercase"
        value={form.referral_code} maxLength={8}
        onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} />
      <label className="brutal-card bg-background flex items-start gap-3 p-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid="auth-age-checkbox"
          checked={ageConfirmed}
          onChange={(e) => setAgeConfirmed(e.target.checked)}
          className="mt-1"
        />
        <span className="text-xs leading-snug">
          I am at least 13 years old and I agree to HAEVEN&apos;s{" "}
          <Link to="/legal/terms" className="underline" data-testid="auth-link-terms">Terms</Link> and{" "}
          <Link to="/legal/privacy" className="underline" data-testid="auth-link-privacy">Privacy Policy</Link>.
        </span>
      </label>
    </>
  );
}

function LoginFields({ form, setForm }) {
  return (
    <>
      <input data-testid="auth-email-input" required type="email" placeholder="Email" className="brutal-input w-full px-4 py-3"
        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <input data-testid="auth-password-input" required type="password" autoComplete="current-password" placeholder="Password" className="brutal-input w-full px-4 py-3"
        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
    </>
  );
}

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const initialRef = (params.get("ref") || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  const [form, setForm] = useState({ email: "", password: "", display_name: "", username: "", referral_code: initialRef });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  // A ref closes the gap before React renders disabled controls.
  const submissionPending = useRef(false);
  const [resetStage, setResetStage] = useState(null);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (submissionPending.current) return;
    if (mode === "register" && !ageConfirmed) {
      toast.error("Please confirm you are 13 or older");
      return;
    }
    submissionPending.current = true;
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else {
        const result = await register({ ...form, age_confirmed: true, referral_code: form.referral_code?.trim() || undefined });
        if (result?.referral?.redeemed) {
          toast.success("Welcome to HAEVEN! Your friend's code unlocked 30 free days of Premium.");
        } else {
          toast.success("Welcome to HAEVEN!");
        }
      }
      if (mode === "login") toast.success("Welcome back");
      navigate("/feed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      submissionPending.current = false;
      setBusy(false);
    }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    if (submissionPending.current) return;
    submissionPending.current = true;
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email: resetEmail }, { timeout: 30000 });
      toast.success("If that email is registered, a reset code has been sent.");
      setResetStage("confirm");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not request a reset code");
    } finally {
      submissionPending.current = false;
      setBusy(false);
    }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (submissionPending.current) return;
    submissionPending.current = true;
    setBusy(true);
    try {
      const r = await api.post("/auth/password-reset/confirm", {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword,
      }, { timeout: 30000 });
      toast.success(r.data?.message || "Password updated. You can log in now.");
      setForm({ ...form, email: resetEmail, password: "" });
      setResetStage(null);
      setResetCode("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally {
      submissionPending.current = false;
      setBusy(false);
    }
  };

  const startReset = () => {
    if (submissionPending.current) return;
    setResetEmail(form.email || "");
    setResetStage("request");
  };

  const leaveReset = () => {
    if (submissionPending.current) return;
    setResetStage(null);
    setResetCode("");
    setNewPassword("");
  };

  const title = resetStage ? "Reset your password" : (mode === "login" ? "Welcome back" : "Make your HAEVEN");
  const subtitle = resetStage === "request"
    ? "Enter your account email and we'll send you a 6-digit reset code."
    : resetStage === "confirm"
      ? "Enter the code from your email and choose a new password."
      : (mode === "login" ? "Log in to your safer social space." : "Three quick details and you're in.");

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-4 py-10" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 font-display text-2xl mb-8">
          <span className="inline-block w-7 h-7 bg-primary border-2 border-foreground rounded-md shadow-brutal-sm" />
          HAEVEN
        </Link>
        <div className="brutal-card p-8 bg-card">
          <h1 className="text-4xl mb-1">{title}</h1>
          <p className="text-muted-foreground mb-6">{subtitle}</p>

          {resetStage === "request" ? (
            <form onSubmit={requestReset} className="space-y-4" data-testid="password-reset-request-form">
              <fieldset disabled={busy} className="space-y-4">
              <input data-testid="password-reset-email-input" required type="email" placeholder="Email" className="brutal-input w-full px-4 py-3"
                value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} />
              <button data-testid="password-reset-send-btn" disabled={busy} type="submit"
                className="brutal-btn bg-primary text-primary-foreground w-full py-3 flex items-center justify-center gap-2 text-lg disabled:opacity-60">
                {busy ? "Sending…" : "Send reset code"}
                <ArrowRight size={18} strokeWidth={2.5} />
              </button>
              </fieldset>
            </form>
          ) : resetStage === "confirm" ? (
            <form onSubmit={confirmReset} className="space-y-4" data-testid="password-reset-confirm-form">
              <fieldset disabled={busy} className="space-y-4">
              <input data-testid="password-reset-code-input" required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="6-digit code"
                className="brutal-input w-full px-4 py-3 tracking-widest" value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
              <input data-testid="password-reset-new-password-input" required type="password" autoComplete="new-password" minLength={12} placeholder="New password (12+ characters)"
                className="brutal-input w-full px-4 py-3" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              <button data-testid="password-reset-confirm-btn" disabled={busy} type="submit"
                className="brutal-btn bg-primary text-primary-foreground w-full py-3 flex items-center justify-center gap-2 text-lg disabled:opacity-60">
                {busy ? "Updating…" : "Reset password"}
                <ArrowRight size={18} strokeWidth={2.5} />
              </button>
              </fieldset>
            </form>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <fieldset disabled={busy} className="space-y-4">
              {mode === "register" ? (
                <RegisterFields form={form} setForm={setForm} ageConfirmed={ageConfirmed} setAgeConfirmed={setAgeConfirmed} />
              ) : (
                <LoginFields form={form} setForm={setForm} />
              )}

              {mode === "login" && (
                <button data-testid="auth-forgot-password-btn" type="button" onClick={startReset}
                  className="block ml-auto text-sm underline underline-offset-4">
                  Forgot password?
                </button>
              )}

              <button data-testid="auth-submit-btn" disabled={busy || (mode === "register" && !ageConfirmed)} type="submit"
                className="brutal-btn bg-primary text-primary-foreground w-full py-3 flex items-center justify-center gap-2 text-lg disabled:opacity-60">
                {busy ? "Hold on…" : (mode === "login" ? "Log in" : "Create account")}
                <ArrowRight size={18} strokeWidth={2.5} />
              </button>
              </fieldset>
            </form>
          )}

          {resetStage ? (
            <button data-testid="password-reset-back-btn" disabled={busy} onClick={leaveReset}
              className="mt-6 text-sm underline underline-offset-4">
              Back to log in
            </button>
          ) : (
            <button data-testid="auth-toggle-mode-btn" disabled={busy} onClick={() => {
              if (!submissionPending.current) setMode(mode === "login" ? "register" : "login");
            }}
              className="mt-6 text-sm underline underline-offset-4">
              {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
